import React, { useCallback, useContext } from "react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import {
  StripeCardElementChangeEvent,
  StripeCardElementOptions,
} from "@stripe/stripe-js";

import config from "../../config";
import { ToastContext } from "../../contexts/ToastContext";
import {
  ReducedOrder,
  CustomerPaymentInitiateResponse,
  CustomerAddress,
} from "../../generated/graphql";
import { LocalisedText } from "../../i18n";
import {
  parseStripeError,
  converStripeErrorToMessageId,
  StripeCardError,
} from "../../models/stripe";
import { PaymentStripe, PaymentMethodState } from "../../types";
import { useCustomerCancelPayment } from "../../useCases/customerPayment";

import AddressOptionItem from "../AddressOptionItem";
import CheckboxField from "../CheckboxField";
import CustomAddressOption from "../CustomAddressOption";
import Button from "../Button";
import NewAddressFields from "../NewAddressFields";
import SwitchInput from "../SwitchInput";

import styles from "./style.module.scss";

const CARD_ELEMENT_OPTIONS: StripeCardElementOptions = {
  hidePostalCode: true,
  style: {
    base: {
      color: "#3d3d3d",
      fontSize: "14px",
      "::placeholder": {
        color: "#a8aab0",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
};

interface CreditCardFormProps {
  order: ReducedOrder;
  paymentMethodState: PaymentStripe;
  setPaymentMethodState: (p: PaymentMethodState) => void;
  isDisabled: boolean;
  isLoading: boolean;
  addressList: CustomerAddress[];
  handleRemoveAddress: (a: CustomerAddress) => void;
  setIsLoading: (isLoading: boolean) => void;
  initiatePayment: () => Promise<CustomerPaymentInitiateResponse | null>;
  paymentQueryPolling: (paymentId: string) => Promise<boolean>;
  onSuccess: () => void;
}

const CreditCardForm: React.FC<CreditCardFormProps> = (
  props: CreditCardFormProps
) => {
  const {
    paymentMethodState,
    setPaymentMethodState,
    setIsLoading,
    isDisabled,
    isLoading,
    order,
    addressList,
    initiatePayment,
    paymentQueryPolling,
    onSuccess,
    handleRemoveAddress,
  } = props;

  const setCreditCardFutureUse = useCallback(
    (isEnabled: boolean) => {
      setPaymentMethodState({
        ...paymentMethodState,
        saveCreditCard: isEnabled,
      });
    },
    [paymentMethodState, setPaymentMethodState]
  );

  const onCountryUpdate = useCallback(
    (country: string) => {
      setPaymentMethodState({
        ...paymentMethodState,
        billingAddress: {
          ...paymentMethodState.billingAddress,
          country,
        },
      });
    },
    [paymentMethodState, setPaymentMethodState]
  );

  const onAddress1Update = useCallback(
    (address1: string) => {
      setPaymentMethodState({
        ...paymentMethodState,
        billingAddress: {
          ...paymentMethodState.billingAddress,
          address1,
        },
      });
    },
    [paymentMethodState, setPaymentMethodState]
  );

  const onAddress2Update = useCallback(
    (address2: string) => {
      setPaymentMethodState({
        ...paymentMethodState,
        billingAddress: {
          ...paymentMethodState.billingAddress,
          address2,
        },
      });
    },
    [paymentMethodState, setPaymentMethodState]
  );

  const setBillingAddressFutureUse = useCallback(
    (isEnabled: boolean) => {
      setPaymentMethodState({
        ...paymentMethodState,
        billingAddress: {
          ...paymentMethodState.billingAddress,
          saveForFutureUse: isEnabled,
        },
      });
    },
    [paymentMethodState, setPaymentMethodState]
  );

  const onAddressSelect = useCallback(
    (index: number) => {
      setPaymentMethodState({
        ...paymentMethodState,
        billingAddress: {
          type: "reuse",
          id: addressList[index].id,
          country: addressList[index].country || "",
          address1: addressList[index].address1 || "",
          address2: addressList[index].address2 || "",
          saveForFutureUse: false,
        },
      });
    },
    [addressList, paymentMethodState, setPaymentMethodState]
  );

  const onNewAddressSelected = useCallback(() => {
    setPaymentMethodState({
      ...paymentMethodState,
      billingAddress: {
        type: "new",
        country: "HKG",
        address1: "",
        address2: "",
        saveForFutureUse: false,
      },
    });
  }, [paymentMethodState, setPaymentMethodState]);

  const onAddressRemove = useCallback(
    (index: number) => {
      const address = addressList[index];
      // Select another address if the selected address is to be removed
      if (
        paymentMethodState.billingAddress.type === "reuse" &&
        paymentMethodState.billingAddress.id === address.id
      ) {
        if (addressList.length === 1) {
          onNewAddressSelected();
        } else {
          if (index === 0) {
            onAddressSelect(1);
          } else {
            onAddressSelect(index - 1);
          }
        }
      }
      handleRemoveAddress(address);
    },
    [
      addressList,
      handleRemoveAddress,
      onNewAddressSelected,
      onAddressSelect,
      paymentMethodState,
    ]
  );

  const onUseShipmentAddressSwitchChanged = useCallback(
    (value: boolean) => {
      setPaymentMethodState({
        ...paymentMethodState,
        isUsingShipmentAddressForBilling: value,
      });
    },
    [paymentMethodState, setPaymentMethodState]
  );

  const onCreditCardChange = useCallback(
    (event: StripeCardElementChangeEvent) => {
      setPaymentMethodState({
        ...paymentMethodState,
        isCreditCardComplete: event.error === undefined && event.complete,
      });
    },
    [paymentMethodState, setPaymentMethodState]
  );

  const { presentToast } = useContext(ToastContext);
  const elements = useElements();
  const stripe = useStripe();
  const cancelPayment = useCustomerCancelPayment();
  const onPayNowClicked = useCallback(async () => {
    if (!elements || !stripe) return;
    const card = elements.getElement(CardElement);
    if (!card) return;
    try {
      setIsLoading(true);
      const intent = await initiatePayment();
      if (!intent) {
        setIsLoading(false);
        return;
      }
      /* eslint-disable @typescript-eslint/camelcase */
      const result = await stripe.confirmCardPayment(intent.token, {
        payment_method: {
          card,
        },
      });
      /* eslint-enable @typescript-eslint/camelcase */
      if (
        result.error &&
        result.error.type === "card_error" &&
        result.error.code
      ) {
        const stripeErrorCode = parseStripeError(result.error.code);
        throw new StripeCardError(stripeErrorCode);
      } else if (result.paymentIntent === undefined || result.error) {
        throw new Error("payment-failed");
      }

      const paymentComplete = await paymentQueryPolling(intent.id);
      if (!paymentComplete) {
        await cancelPayment(intent.id);
        throw new Error("payment-not-captured");
      }
      onSuccess();
    } catch (e) {
      setIsLoading(false);
      if (e instanceof StripeCardError) {
        const messageId = converStripeErrorToMessageId(e.code);
        presentToast({ content: messageId }, true);
      } else {
        presentToast({ content: "screen.paymentInfoScreen.fail" }, true);
      }
    }
  }, [
    stripe,
    setIsLoading,
    elements,
    initiatePayment,
    paymentQueryPolling,
    onSuccess,
    presentToast,
    cancelPayment,
  ]);

  return (
    <div className={styles.creditCardInfo}>
      <div className={styles.description}>
        <LocalisedText id="view.paymentMethodForm.creditCardDescription" />
      </div>
      <div className={styles.creditCardField}>
        <CardElement
          id="card-element"
          options={CARD_ELEMENT_OPTIONS}
          onChange={onCreditCardChange}
        />
      </div>
      {config.feature.stripeTokenizeCard && (
        <CheckboxField
          checkboxDescriptionId="view.paymentMethodForm.saveCreditCardInfo"
          isEnabled={paymentMethodState.saveCreditCard}
          setEnabled={setCreditCardFutureUse}
        />
      )}
      <div className={styles.billingAddress}>
        <div className={styles.title}>
          <LocalisedText id="view.paymentMethodForm.billingAddress" />
        </div>
        {order.shipmentRequired && (
          <div className={styles.useShipmentAddressSwitch}>
            <SwitchInput
              checked={paymentMethodState.isUsingShipmentAddressForBilling}
              onChange={onUseShipmentAddressSwitchChanged}
              disabled={isLoading}
            />
            <div className={styles.switchDescription}>
              <LocalisedText id="view.paymentMethodForm.useShipmentAddress" />
            </div>
          </div>
        )}
        {!paymentMethodState.isUsingShipmentAddressForBilling && (
          <div className={styles.addressFields}>
            {addressList.length === 0 && (
              <NewAddressFields
                country={paymentMethodState.billingAddress.country}
                address1={paymentMethodState.billingAddress.address1}
                address2={paymentMethodState.billingAddress.address2}
                saveForFutureUse={
                  paymentMethodState.billingAddress.saveForFutureUse
                }
                setCountry={onCountryUpdate}
                setAddress1={onAddress1Update}
                setAddress2={onAddress2Update}
                setSaveForFutureUse={setBillingAddressFutureUse}
                isDisabled={isLoading}
                checkboxDescriptionId="view.addressForm.saveBillingForFuture"
              />
            )}
            {addressList.length > 0 && (
              <div className={styles.options}>
                {addressList.map((address, index) => {
                  return (
                    <AddressOptionItem
                      key={address.id}
                      country={address.country || ""}
                      address1={address.address1 || ""}
                      address2={address.address2 || ""}
                      index={index}
                      isSelected={
                        paymentMethodState.billingAddress.type === "reuse" &&
                        paymentMethodState.billingAddress.id === address.id
                      }
                      onSelect={onAddressSelect}
                      onRemove={onAddressRemove}
                    />
                  );
                })}
                <CustomAddressOption
                  country={paymentMethodState.billingAddress.country}
                  address1={paymentMethodState.billingAddress.address1}
                  address2={paymentMethodState.billingAddress.address2}
                  saveForFutureUse={
                    paymentMethodState.billingAddress.saveForFutureUse
                  }
                  setCountry={onCountryUpdate}
                  setAddress1={onAddress1Update}
                  setAddress2={onAddress2Update}
                  setSaveForFutureUse={setBillingAddressFutureUse}
                  isSelected={paymentMethodState.billingAddress.type === "new"}
                  onSelect={onNewAddressSelected}
                  isDisabled={isLoading}
                  checkboxDescriptionId="view.addressForm.saveBillingForFuture"
                  titleId="view.addressForm.enterNewBillingInfo"
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.buttons}>
        <Button
          isDisabled={isDisabled}
          isLoading={isLoading}
          onClick={onPayNowClicked}
        >
          <LocalisedText id="screen.paymentInfoScreen.payNow" />
        </Button>
      </div>
    </div>
  );
};

export default CreditCardForm;
